import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Code2, Radio, Megaphone, Globe } from "lucide-react";

import eltren from "@/assets/partners/eltren.jpg";
import lanin from "@/assets/partners/lanin.jpg";
import merlin from "@/assets/partners/merlin.jpg";
import mico from "@/assets/partners/mico.jpg";

const partners = [
  { name: "Merlin", descKey: "hub.merlin", image: merlin, icon: Code2, gradient: "from-[#E92052] to-[#602d90]", taglineKey: "hub.merlin.tagline" },
  { name: "El Tren", descKey: "hub.eltren", image: eltren, icon: Radio, gradient: "from-[#602d90] to-[#E92052]", taglineKey: "hub.eltren.tagline" },
  { name: "Mico", descKey: "hub.mico", image: mico, icon: Megaphone, gradient: "from-[#E92052] to-[#602d90]", taglineKey: "hub.mico.tagline" },
  { name: "Lanin", descKey: "hub.lanin", image: lanin, icon: Globe, gradient: "from-[#602d90] to-[#E92052]", taglineKey: "hub.lanin.tagline" },
];

const accentColor = "#E92052";
const brandColors = ["#E92052", "#602d90", "#fda700", "#08214A"];

const HubSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { t } = useLanguage();
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [colorIdx, setColorIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setColorIdx((prev) => (prev + 1) % brandColors.length), 2500);
    return () => clearInterval(interval);
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [140, 0, 0, -70]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 1, 1, 0.3]);

  return (
    <section id="hub" className="py-24 md:py-32 px-6 bg-[#0D0D0D] text-white overflow-hidden" ref={ref}>
      <motion.div className="max-w-7xl mx-auto" style={{ y: contentY, opacity: contentOpacity }}>
        {/* Header */}
        <motion.div
          className="mb-16 md:mb-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start"
          initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
          animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Left: tag + title + dream team */}
          <div>
            <motion.p
              className="text-xs uppercase tracking-[0.25em] font-bold mb-4 text-primary-foreground"
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {t("hub.tag")}
            </motion.p>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight lowercase leading-[1.05] mb-6">
              <span className="overflow-hidden inline-block">
                <motion.span
                  className="inline-block"
                  initial={{ y: "100%" }}
                  animate={inView ? { y: "0%" } : {}}
                  transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                >
                  {t("hub.title.line1")}
                </motion.span>
              </span>
              <br />
              <span className="overflow-hidden inline-block">
                <motion.span
                  className="inline-block"
                  initial={{ y: "100%" }}
                  animate={inView ? { y: "0%" } : {}}
                  transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundImage: `linear-gradient(90deg, ${brandColors[colorIdx]}, ${brandColors[(colorIdx + 1) % brandColors.length]})`,
                  }}
                >
                  {t("hub.title.highlight")}
                </motion.span>
              </span>{" "}
              <span className="overflow-hidden inline-block">
                <motion.span
                  className="inline-block"
                  initial={{ y: "100%" }}
                  animate={inView ? { y: "0%" } : {}}
                  transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  {t("hub.title.line2")}
                </motion.span>
              </span>
            </h2>
            <motion.div
              className="mt-6 border-l-2 pl-5 border-secondary"
              initial={{ opacity: 0, x: -20, filter: "blur(6px)" }}
              animate={inView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
              transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-base md:text-lg text-white/80 leading-relaxed font-medium italic">
                {t("hub.desc").split("dream team").map((part, idx, arr) =>
                  idx < arr.length - 1 ? (
                    <span key={idx}>{part}<span className="text-secondary font-bold not-italic">dream team</span></span>
                  ) : (
                    <span key={idx}>{part}</span>
                  )
                )}
              </p>
            </motion.div>
          </div>

          {/* Right: body paragraphs */}
          <div className="flex flex-col gap-5 md:pt-12">
            <motion.p
              className="text-base md:text-lg text-white/70 leading-relaxed font-medium"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              {t("hub.body1")}
            </motion.p>
            <motion.p
              className="text-sm text-white/40 leading-relaxed font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              {t("hub.body2")}
            </motion.p>
          </div>
        </motion.div>

        {/* Expandable accordion cards */}
        <div className="flex flex-col md:flex-row gap-3 md:h-[320px]">
          {partners.map((partner, i) => {
            const isExpanded = expandedIdx === i;
            const Icon = partner.icon;

            return (
              <motion.div
                key={partner.name}
                className="relative rounded-2xl overflow-hidden cursor-pointer"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                onMouseEnter={() => setExpandedIdx(i)}
                onMouseLeave={() => setExpandedIdx(null)}
                layout
                style={{
                  flex: isExpanded ? 3 : 1,
                  transition: "flex 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              >
                {/* Gradient border effect */}
                <motion.div
                  className={`absolute inset-0 z-0 rounded-2xl bg-gradient-to-br ${partner.gradient}`}
                  animate={{ opacity: isExpanded ? 0.15 : 0 }}
                  transition={{ duration: 0.4 }}
                />

                {/* Inner card */}
                <div
                  className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8 rounded-2xl border transition-all duration-500 backdrop-blur-sm"
                  style={{
                    borderColor: isExpanded ? "rgba(233, 32, 82, 0.3)" : "rgba(255,255,255,0.06)",
                    backgroundColor: isExpanded ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
                  }}
                >
                  {/* Top: Logo */}
                  <div>
                    <motion.img
                      src={partner.image}
                      alt={partner.name}
                      className="h-24 md:h-36 w-auto object-contain brightness-0 invert mb-4 transition-opacity duration-300"
                      style={{ opacity: isExpanded ? 1 : 0.5 }}
                      animate={{ scale: isExpanded ? 1.08 : 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    />

                    <motion.span
                      className="text-[10px] uppercase tracking-[0.2em] font-semibold block mt-1"
                      animate={{ color: isExpanded ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.2)" }}
                      transition={{ duration: 0.3 }}
                    >
                      {t(partner.taglineKey)}
                    </motion.span>
                  </div>

                  {/* Bottom: Description - only visible when expanded */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: 10, filter: "blur(4px)" }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-auto"
                      >
                        <div className="w-10 h-[2px] mb-4 bg-gradient-to-r from-[#E92052] to-[#602d90] rounded-full" />
                        <p className="text-sm text-white/60 leading-relaxed max-w-xs">
                          {t(partner.descKey)}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Growing indicator */}
        <motion.div
          className="mt-10 flex items-center justify-center gap-3"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 1.2 }}
        >
          <motion.div
            className="w-2 h-2 rounded-full bg-gradient-to-r from-[#E92052] to-[#602d90]"
            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <span className="text-xs uppercase tracking-[0.2em] text-white/30">{t("hub.growing")}</span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HubSection;
